import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60000; // 1 minute

// Play a short attention-grabbing beep using Web Audio API (no external assets)
const playCallbackChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    // Two-tone chime: 880Hz -> 660Hz
    [
      { freq: 880, start: 0, dur: 0.18 },
      { freq: 660, start: 0.2, dur: 0.28 },
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    setTimeout(() => ctx.close && ctx.close(), 800);
  } catch (e) {
    console.debug('CallbackNotifier: chime unavailable', e);
  }
};

const CallbackNotifier = ({ user }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const showNotification = (cb) => {
      // Play chime sound to alert operator
      playCallbackChime();

      const when = new Date(cb.callback_date);
      const timeStr = when.toLocaleTimeString(language === 'it' ? 'it-IT' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });
      const title = t('notif_callback_title');
      const body = `${cb.lead_name} · ${cb.lead_phone} · ${timeStr}`;

      // Browser notification
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, { body, tag: cb.id });
          n.onclick = () => {
            window.focus();
            navigate(`/leads/${cb.lead_id}`);
            n.close();
          };
        } catch (e) {
          console.debug('CallbackNotifier: Notification API failed', e);
        }
      }

      // In-app toast
      toast(
        <div className="flex items-start gap-3" data-testid={`callback-alert-${cb.id}`}>
          <div className="mt-0.5">
            <Clock size={20} style={{ color: '#F1C21B' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold">{title}</div>
            <div className="text-sm">
              <span className="font-medium">{cb.lead_name}</span>
              {' · '}
              <span className="font-mono">{cb.lead_phone}</span>
            </div>
            <div className="text-xs opacity-80">{timeStr}{cb.reason ? ` · ${cb.reason}` : ''}</div>
            <button
              type="button"
              onClick={() => navigate(`/leads/${cb.lead_id}`)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded"
              style={{ backgroundColor: '#0F62FE', color: 'white' }}
              data-testid={`callback-alert-open-${cb.id}`}
            >
              <Phone size={12} />
              {t('notif_open_lead')}
            </button>
          </div>
        </div>,
        {
          duration: 20000,
          id: `cb-${cb.id}`,
        }
      );
    };

    const poll = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.get(`${API}/callbacks/upcoming`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        for (const cb of res.data || []) {
          if (seenRef.current.has(cb.id)) continue;
          seenRef.current.add(cb.id);
          showNotification(cb);
          // Mark as notified server-side to avoid re-showing across sessions/tabs
          axios
            .post(`${API}/callbacks/${cb.id}/notify`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => {});
        }
      } catch (e) {
        console.debug('CallbackNotifier: upcoming poll failed', e?.response?.status || e?.message);
      }
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, language, navigate, t]);

  return null;
};

export default CallbackNotifier;
